import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { LAMBDA_BASE_URL } from "../../constants";

const SubscribeSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userOauthCode = searchParams.get("code");
  const scopes = searchParams.get("scope")?.split(" ") || [];

  const isCombo =
    scopes.includes("https://www.googleapis.com/auth/gmail.readonly") &&
    scopes.includes("https://www.googleapis.com/auth/gmail.compose");
  const isEmailSync = scopes.includes(
    "https://www.googleapis.com/auth/gmail.readonly"
  );

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn: (code: string) =>
      axios.post(LAMBDA_BASE_URL, { userOauthCode: code, scopes }),
  });

  useEffect(() => {
    if (userOauthCode) {
      mutate(userOauthCode);
    }
  }, [userOauthCode, mutate]);

  return userOauthCode ? (
    isPending ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          height: 300,
        }}>
        <CircularProgress />
      </Box>
    ) : (
      <Card elevation={5} sx={{ width: 500, margin: "auto" }}>
        <CardContent>
          <Container maxWidth='sm' sx={{ mt: 8 }}>
            <Typography variant='h5' gutterBottom>
              ✅ You're all set!
            </Typography>
            <Typography variant='body1' sx={{ mt: 2 }}>
              {isCombo
                ? "Jarvis is now fully connected with your Gmail."
                : isEmailSync
                ? "Automatic email syncing is now active."
                : "Jarvis can now create Gmail drafts for you as part of Sales Automation."}
            </Typography>
            <Typography variant='body2' sx={{ mt: 2 }}>
              {isCombo
                ? `Your external emails will be synced to Zoho CRM Notes automatically.
                Jarvis can also generate Gmail drafts for faster replies.`
                : isEmailSync
                ? `Jarvis will check your inbox every hour. Only relevant threads
              will be added to Zoho CRM Notes — emails within your company
              domain will be skipped.`
                : `You’ll be able to review and send these drafts manually, saving time while keeping full control over your messages.`}
            </Typography>
            {isEmailSync && (
              <Typography variant='body2' sx={{ mt: 1 }}>
                You’ll be notified if any contacts are missing in the CRM.
              </Typography>
            )}
            {isError && (
              <Typography variant='body1' color='error' mt='8px'>
                ❌ An error occurred while subscribing your account.
                <br />
                {error.message || "Please try again later."}
              </Typography>
            )}
          </Container>
        </CardContent>
      </Card>
    )
  ) : (
    <h1>This page does not exist</h1>
  );
};

export default SubscribeSuccess;
